import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Alert, Button, TextField } from '../components/ui/index.tsx';
import { api, ApiError } from '../lib/api.ts';
import { formatMoney } from '../lib/money.ts';
import { useSession } from '../state/session.tsx';
import './auth.css';

const REGIMES = [
  { id: 'IN_GST', name: 'India GST', meta: 'CGST/SGST/IGST' },
  { id: 'AE_VAT', name: 'UAE VAT', meta: '5% standard' },
  { id: 'GENERIC', name: 'Simple tax', meta: 'One rate' },
  { id: 'NONE', name: 'No tax', meta: 'Untaxed' },
];

const POINTS = [
  {
    title: 'One account, every company',
    body: 'Switch between businesses without signing out. Separate books, numbering and branding for each.',
  },
  {
    title: 'Tax that knows where it is',
    body: 'GST splits into CGST and SGST inside a state and IGST across one. UAE VAT zero-rates exports on its own.',
  },
  {
    title: 'Arithmetic you can reconcile',
    body: 'Every amount is stored in whole paise. No floating point ever touches a figure you will check against a bank statement.',
  },
];

export function Auth() {
  const { signIn, signUp } = useSession();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [taxRegime, setTaxRegime] = useState('IN_GST');
  const [plan, setPlan] = useState('free');
  const [region, setRegion] = useState<string | null>(null);

  // Only fetched for signup: someone signing in has no use for a price list.
  const { data: catalogue } = useQuery({
    queryKey: ['signup-plans', region],
    queryFn: () =>
      api.get<{
        region: string;
        regions: { id: string; label: string; currency: string }[];
        plans: { id: string; name: string; priceMinor: number; currency: string }[];
      }>(region ? `/api/billing/plans?region=${region}` : '/api/billing/plans'),
    enabled: mode === 'signup',
  });

  const regions = catalogue?.regions ?? [];
  const regionLabel = regions.find((entry) => entry.id === catalogue?.region)?.label ?? '';
  // A single toggle rather than a dropdown: the guess is usually right, and
  // this is only here for when it is not.
  const currentIndex = regions.findIndex((entry) => entry.id === catalogue?.region);
  const nextRegion = regions[(currentIndex + 1) % Math.max(1, regions.length)]?.id ?? null;
  const nextRegionLabel = regions[(currentIndex + 1) % Math.max(1, regions.length)]?.label ?? '';
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'signin') await signIn(email, password);
      else
        await signUp({
          email,
          password,
          name,
          businessName,
          taxRegime,
          plan,
          // Send back the list we quoted against, so the price shown here is
          // the one that gets stored rather than being guessed again.
          priceRegion: catalogue?.region,
        });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not reach the server. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <aside className="auth__aside">
        <div className="auth__brand">
          Vela
          <span className="auth__dot" aria-hidden="true" />
          <span className="auth__byline">by Atronova</span>
        </div>

        <div>
          <h1 className="auth__headline">
            Invoicing for people who run <em>more than one</em> thing.
          </h1>
          <p className="auth__lede">
            Quotations, invoices, purchase orders, expenses and tax returns — kept straight across
            every company you own.
          </p>

          <div className="auth__points">
            {POINTS.map((point, index) => (
              <div className="auth__point" key={point.title}>
                <span className="auth__point-index" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <p className="auth__point-title">{point.title}</p>
                  <p className="auth__point-body">{point.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="auth__footnote">
          Supports India GST, UAE VAT, and a plain single-rate regime for everywhere else.
        </p>
      </aside>

      <section className="auth__panel">
        <form className="auth__form stack stack-4" onSubmit={submit} noValidate>
          <div>
            <h2 className="auth__title">
              {mode === 'signin' ? 'Sign in' : 'Create your account'}
            </h2>
            <p className="auth__subtitle">
              {mode === 'signin'
                ? 'Pick up where you left off.'
                : 'Start with one business — add the rest whenever you like.'}
            </p>
          </div>

          {error && <Alert tone="error">{error}</Alert>}

          {mode === 'signup' && (
            <TextField
              label="Your name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
              required
            />
          )}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            hint={mode === 'signup' ? 'At least 8 characters.' : undefined}
            required
          />

          {mode === 'signup' && (
            <>
              <TextField
                label="Business name"
                value={businessName}
                onChange={(event) => setBusinessName(event.target.value)}
                placeholder="Atronova Labs"
                autoComplete="organization"
                required
              />

              <div className="field">
                <span className="field__label">Tax regime</span>
                <div className="regime-choice">
                  {REGIMES.map((regime) => (
                    <button
                      key={regime.id}
                      type="button"
                      className="regime-option"
                      aria-pressed={taxRegime === regime.id}
                      onClick={() => setTaxRegime(regime.id)}
                    >
                      <span className="regime-option__name">{regime.name}</span>
                      <span className="regime-option__meta">{regime.meta}</span>
                    </button>
                  ))}
                </div>
                <p className="field__hint">You can change this later, per business.</p>
              </div>

              <div className="field">
                <span className="field__label">Plan</span>
                <div className="regime-choice">
                  {(catalogue?.plans ?? []).map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className="regime-option"
                      aria-pressed={plan === option.id}
                      onClick={() => setPlan(option.id)}
                    >
                      <span className="regime-option__name">{option.name}</span>
                      <span className="regime-option__meta">
                        {option.priceMinor === 0
                          ? 'Free forever'
                          : `${formatMoney(option.priceMinor, option.currency)} / month`}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="field__hint">
                  Every account starts with 14 days of everything, whichever you pick — there is
                  nothing to pay today.{' '}
                  {catalogue && (
                    <>
                      Prices shown for {regionLabel}.{' '}
                      <button
                        type="button"
                        className="linklike"
                        onClick={() => setRegion(nextRegion)}
                      >
                        Show {nextRegionLabel}
                      </button>
                    </>
                  )}
                </p>
              </div>
            </>
          )}

          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </Button>

          <p className="auth__switch">
            {mode === 'signin' ? "Don't have an account? " : 'Already have one? '}
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'signin' ? 'signup' : 'signin');
                setError(null);
              }}
            >
              {mode === 'signin' ? 'Create one' : 'Sign in'}
            </button>
          </p>

          {mode === 'signin' && (
            <p className="auth__demo">
              Seeded a demo database? Sign in with <code>demo@vela.test</code> and{' '}
              <code>vela demo 2026</code>.
            </p>
          )}
        </form>
      </section>
    </div>
  );
}
